/**
 * Runtime configuration.
 *
 * Values come from `window.__APP_CONFIG__` (see public/config.js), which
 * docker-entrypoint.sh fills in at container start. That keeps one built image
 * promotable from local → TEST → production. When those placeholders haven't been
 * substituted — i.e. `vite dev` straight from the repo — we fall back to the
 * build-time `VITE_*` env vars.
 *
 * IMPORTANT: the add-in must be served from a host that shares its registrable
 * domain with BOL2. BOL2's `auth` cookie is set by CodeIgniter's `set_cookie()`,
 * which uses the 7-argument form of PHP's `setcookie()` and so cannot set
 * `SameSite`. Browsers therefore default it to `SameSite=Lax`, which is evaluated
 * against the *top-level* site:
 *
 *   - Task pane  → top-level site is outlook.office.com → cross-site → cookie NOT sent.
 *   - Dialog     → its own top-level window on our origin → same-site as BOL2 → cookie IS sent.
 *
 * So sign-in happens in a dialog, and the origins must match at the registrable
 * domain: `outlook-helper.apps.test` with `bol2.apps.test` locally (`.test` is a
 * public suffix, so `apps.test` is the registrable domain), or
 * `*.myblockonline.co.uk` in a deployed environment.
 */

type AppConfig = {
    bol2BaseUrl?: string
    bol2LoginPath?: string
    bol2LogoutPath?: string
}

declare global {
    interface Window {
        __APP_CONFIG__?: AppConfig
    }
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '')
}

function setting(runtime: string | undefined, buildTime: string | undefined, name: string): string {
    const value = runtime?.trim() || buildTime?.trim()
    if (!value) {
        throw new Error(
            `Missing configuration "${name}". Set it via the container environment ` +
            `(see docker-compose.yml) or as VITE_${name.replace(/([A-Z])/g, '_$1').toUpperCase()} for local dev.`,
        )
    }
    return trimTrailingSlash(value)
}

const runtimeConfig: AppConfig = typeof window !== 'undefined' ? window.__APP_CONFIG__ ?? {} : {}

/** BOL2 origin, e.g. https://bol2.apps.test */
export const BOL2_BASE_URL = setting(
    runtimeConfig.bol2BaseUrl,
    import.meta.env.VITE_BOL2_BASE_URL,
    'bol2BaseUrl',
)

/** Base for all external API calls. */
export const API_BASE_URL = `${BOL2_BASE_URL}/api/v1`

/**
 * BOL2's workbench login page, which also handles 2FA.
 *
 * The `/rel3/` prefix is not optional: BOL2 serves two apps from one origin. The
 * root is a Slim 4 app (the `/api/v1` REST API); CodeIgniter — which owns the
 * workbench and its login screen — is mounted under `/rel3/` (see
 * `$config['base_url']` in rel3/application/config/config.php). BOL2's root
 * .htaccess rewrites anything that is not a real file to the Slim front
 * controller, so `/admin/WorkbenchLoginController` does not fail as a missing
 * page: it reaches Slim, matches no route, and returns
 * `{"success":false,"message":"Not found."}` as JSON.
 */
export const BOL2_LOGIN_URL = `${BOL2_BASE_URL}${runtimeConfig.bol2LoginPath?.trim() ||
    import.meta.env.VITE_BOL2_LOGIN_PATH ||
    '/rel3/admin/WorkbenchLoginController'
    }`

/**
 * BOL2's workbench logout, which is the only endpoint that clears *both* halves of
 * the browser-side session: it calls `sess_destroy()` on the CodeIgniter session and
 * expires the `auth` cookie.
 *
 * Both matter for signing out of the add-in. Dropping our own copy of the JWT leaves
 * the webview holding a live `auth` cookie and `PHPSESSID`, and a stale `auth` cookie
 * plus a live CI session is precisely the state the add-in cannot recover from:
 * WorkbenchLoginController's constructor short-circuits an already-signed-in user
 * straight to `?redirect` without ever calling `do_login()`, so no fresh cookie is
 * issued and the sign-in dialog loops.
 *
 * Same `/rel3/` prefix caveat as the login URL above.
 */
export const BOL2_LOGOUT_URL = `${BOL2_BASE_URL}${runtimeConfig.bol2LogoutPath?.trim() ||
    import.meta.env.VITE_BOL2_LOGOUT_PATH ||
    '/rel3/admin/WorkbenchLogout'
    }`

/**
 * Where the add-in is served from. Taken from the current origin rather than
 * configured: the dialog pages must be same-origin anyway (only same-origin pages may
 * call `messageParent`), so deriving it removes a whole class of misconfiguration.
 */
export const ADDIN_BASE_URL = trimTrailingSlash(window.location.origin)

export const AUTH_DIALOG_URL = `${ADDIN_BASE_URL}/auth/dialog.html`
export const AUTH_CALLBACK_URL = `${ADDIN_BASE_URL}/auth/callback.html`
export const AUTH_LOGOUT_URL = `${ADDIN_BASE_URL}/auth/logout.html`

/**
 * Max characters of email body copied into a new ticket's description.
 * `ticket_master.ticket_description` is a MySQL TEXT column (~64KB), so this limit
 * is about keeping tickets readable rather than about the column.
 */
export const MAX_DESCRIPTION_CHARS = 4000
